/* lib/adapters/subscription.js — `subscriptionReal`: сёрфейс под BillingView/PlanCard/PaymentsList,
 * поверх боевого subscription-клиента (через unwrap → throw{status} для useFakeQuery/catch-веток).
 *
 * Backend `/me/` НЕСТЁД: plan/pending_plan — объекты, usage без суффикса `_used`, price/amount — строки.
 * Адаптер плющит в плоскую форму, которую ждёт UI: sub.plan = slug, usage.*_used, features dict→array. */

import { unwrap } from '../api/unwrap.js';
import * as api from '../api/subscription.js';
import * as linkApi from '../api/salesConsole.js';

const num = (v) => (v == null ? 0 : Number(v));

/* Флаг фичи — только `true` (контракт модели: {слаг: bool}). Число или список — это
   ПАРАМЕТР тарифа, а не фича: `features.expiry_notice_days = 14 | [14, 30]` включает
   ранние напоминания об окончании доступа. Раньше любое истинное значение делалось
   слагом, и карточка текущего тарифа показывала галочку «expiry_notice_days».
   Строковое значение — прежний формат «значение = слаг». */
function featuresToArray(f) {
  if (Array.isArray(f)) return f;
  if (f && typeof f === 'object') {
    return Object.entries(f).map(([k, v]) => (typeof v === 'string' ? v : (v === true ? k : null))).filter(Boolean);
  }
  return [];
}

/* Аудитория тарифа — решение СЕРВЕРА (поле `audience`). Фолбэк на прежний
   косвенный признак (фича team_members) нужен только для старых закэшированных
   ответов и живёт ЗДЕСЬ, в одном месте: витрина тарифов и визард создания
   пространства выводили аудиторию каждый по-своему, и правила разъезжались —
   тариф с пустыми фичами молча уезжал в личную вкладку и исчезал из визарда. */
const audienceOf = (audience, features) => audience
  || (features.includes('team_members') ? 'business' : 'personal');

/* Тариф ИЗ ПОДПИСКИ в форме карточки (та же форма, что даёт adaptPlan).
   Витрина `/plans/` ws-scoped: закрытый (сетевой) тариф сервер кладёт в список
   ТОЛЬКО тому пространству, что сейчас активно, — а тарифную панель можно
   открыть на другом пространстве, не переключая активное. Тогда своего тарифа
   в витрине нет, и рисовать карточку «Текущий тариф» не из чего.
   Чего в `/me/` нет и что здесь не выдумывается: `is_default`; аудитория
   выводится из фич тем же правилом, что и в витрине. */
function adaptPlanFromSubscription(plan, limits) {
  const lim = limits || {};
  return {
    slug: plan.slug,
    name: plan.name || '',
    is_paid: plan.is_paid,
    price_rub: num(plan.price_rub),
    // Срок теперь приходит в /me/.plan — без него PlanCard подписывал цену
    // «/ мес» по умолчанию, и тариф на 90 дней врал бы клиенту в 3 раза.
    period_days: plan.period_days,
    features: featuresToArray(plan.features),
    audience: audienceOf(undefined, featuresToArray(plan.features)),
    // Закрытый тариф по договору снят с продажи и в витрине его нет — карточка
    // строится отсюда, и без признака она написала бы «Бесплатно» (planPrice.js).
    is_private: plan.is_private === true,
    // Разовый тариф (акция) — карточка не предлагает «Возобновить продление»: сервер его
    // отбивает (`not_renewable`). Нет поля (старый ответ) — продлеваемый, как в витрине
    // (adaptPlan). Источник — `/me/`.plan.renewable (S42, раунд 4).
    renewable: plan.renewable !== false,
    // лимиты периода живут в me.limits, а не в me.plan
    chat_messages_per_30d: lim.chat_messages_per_30d,
    voice_seconds_per_30d: lim.voice_seconds_per_30d,
    max_documents: plan.max_documents,
    max_storage_mb: plan.max_storage_mb,
    max_file_size_mb: plan.max_file_size_mb,
  };
}

// /me/ (нестед) → плоский sub, который читают BillingView + PlanCard.
function adaptSubscription(me) {
  const plan = me.plan || {};
  return {
    plan: plan.slug || null,            // BillingView: plans.find(p => p.slug === sub.plan)
    plan_name: plan.name || '',
    // Запасной источник карточки текущего тарифа, когда его нет в витрине.
    plan_details: plan.slug ? adaptPlanFromSubscription(plan, me.limits) : null,
    pending_plan: me.pending_plan ? me.pending_plan.slug : null,
    pending_plan_name: me.pending_plan ? me.pending_plan.name : null,
    // Цена и срок запланированного тарифа: платный включится оплатой продления по
    // ЕГО цене — карточка говорит, что и когда спишут. Цена — снимок, на который
    // клиент согласился (billing.renewal_terms), а не текущий прайс тарифа: консоль
    // могла его сменить. Старый ответ без полей → null.
    pending_plan_price: me.pending_plan && me.pending_plan.price_rub != null
      ? num(me.pending_plan.price_rub) : null,
    pending_plan_period_days: me.pending_plan && me.pending_plan.period_days != null
      ? me.pending_plan.period_days : null,
    // Чем оплатится следующий период: 'card' | 'invoice' | 'manual' (правило кронов,
    // billing.renewal_channel). По нему экран спрашивает согласие перед понижением.
    // Нет ключа (старый бэкенд) → null.
    renewal_channel: me.renewal_channel || null,
    // Отложенный тариф уже ОПЛАЧЕН (акция в очереди) — отменить его нельзя.
    pending_plan_prepaid: !!(me.pending_plan && me.pending_plan.prepaid),
    is_active: me.is_active,
    auto_renew: me.auto_renew,
    // Сколько РЕАЛЬНО спишется при продлении: при смене цены тарифа действующим
    // подписчикам закрепляется прежняя (billing.renewal_price). Старый бэкенд ключа
    // не присылает → null, и карточка показывает цену тарифа, как раньше.
    renewal_price_rub: me.renewal_price_rub != null && me.renewal_price_rub !== ''
      ? String(me.renewal_price_rub) : null,
    expires_at: me.expires_at,
    period: me.period || null,
    usage: {                            // бэк: usage.chat_messages → UI ждёт chat_messages_used
      chat_messages_used: me.usage ? me.usage.chat_messages : null,
      voice_seconds_used: me.usage ? me.usage.voice_seconds : null,
      documents_used: me.usage ? me.usage.documents : null,
      storage_mb_used: me.usage ? me.usage.storage_mb : null,
    },
    // {chat_messages_per_30d, voice_seconds_per_30d, max_documents, max_storage_mb,
    //  max_file_size_mb} — совпадает с UI; лимиты файлов рисуем только для бизнеса.
    limits: me.limits || null,
    // Открыт ли пространству платный доступ: {ok, state}, где state —
    // active | grace | expired | trial_over | no_plan (subscriptions/access.py).
    // Старый бэкенд ключа не присылает → null, и карточка тарифа рисуется как
    // раньше (components/patterns/planAccess.js).
    access: me.access || null,
    // Может ли ТЕКУЩИЙ пользователь ещё взять свой единственный бесплатный
    // «Быстрый старт». Отдаём как есть, без приведения к bool: «ключа нет»
    // (старый бэкенд) обязано отличаться от false — иначе мастер создания
    // перестал бы предлагать бесплатный период всем (screens/settings/quickStart.js).
    quick_start_available: me.quick_start_available,
    // Персональный тариф, выданный ТЕКУЩЕМУ пользователю (как и флаг выше — про
    // зрителя, а не про активное пространство): мастер создания показывает его
    // первой карточкой вместо «Быстрого старта». Нет выдачи или ключа (старый
    // бэкенд) → null, и мастер ведёт себя как раньше.
    personal_plan: adaptPersonalPlan(me.personal_plan),
  };
}

/* `/me/`.personal_plan → {plan, duration_days, workspaces_used, workspaces_max} | null.
   `plan` — та же форма, что у элемента витрины `/plans/`, и идёт через тот же
   adaptPlan: мастер кладёт этот тариф в одну линейку с тарифами витрины.
   duration_days: null — бессрочно, N — пространство будет открыто N дней. Это
   срок ДОСТУПА; `plan.period_days` — окно квот, сроком его показывать нельзя.
   Ключ без тарифа-объекта отдаём как null, а не роняем разбор: подписку читают
   тарифная панель, оплата и ассистент, а персональный тариф нужен одному мастеру.
   Поля перечислены явно — новое поле выдачи сюда надо добавлять руками (S13). */
function adaptPersonalPlan(raw) {
  if (!raw || typeof raw !== 'object') return null;
  if (!raw.plan || typeof raw.plan !== 'object') return null;
  return {
    plan: adaptPlan(raw.plan),
    duration_days: raw.duration_days == null ? null : raw.duration_days,
    workspaces_used: raw.workspaces_used,
    workspaces_max: raw.workspaces_max,
  };
}

function adaptPlan(p) {
  return {
    slug: p.slug, name: p.name, is_paid: p.is_paid, is_default: p.is_default,
    price_rub: num(p.price_rub),        // DRF строка → число (PlanCard: price === 0 / price > currentPrice)
    period_days: p.period_days,         // PlanCard: суффикс цены (/мес, /6 мес, /год…)
    // Продлевается ли тариф автосписанием → нужна ли галочка согласия перед
    // оплатой картой (lib/recurringConsent.js). Нет поля (старый ответ) —
    // считаем продлеваемым: спросить лишний раз безопаснее, чем не спросить.
    // Источник — `_plan_dict` в backend/subscriptions/views.py.
    renewable: p.renewable !== false,
    features: featuresToArray(p.features),
    chat_messages_per_30d: p.chat_messages_per_30d,
    voice_seconds_per_30d: p.voice_seconds_per_30d,
    // лимиты базы знаний (PlanCard рисует их только для бизнес-тарифов)
    max_documents: p.max_documents,
    max_storage_mb: p.max_storage_mb,
    max_file_size_mb: p.max_file_size_mb,
    // Адаптер перечисляет поля ЯВНО, поэтому новые сюда надо добавлять руками —
    // без этого они молча теряются между API и экраном (так и вышло: сервер
    // помечал тариф развилкой, а фронт её не видел и уводил сразу в оплату).
    // вкладка тарифа: personal | business (фолбэк на team_members — в audienceOf)
    audience: audienceOf(p.audience, featuresToArray(p.features)),
    warn_personal: p.warn_personal,     // развилка перед оплатой (решение сервера)
    // Закрытый (сетевой) тариф доезжает до витрины ровно в одном случае — когда
    // клиент на нём и сидит: `PlansListView` отдаёт `Q(is_private=False) | Q(slug=текущий)`.
    // Признак нужен, чтобы карточка текущего тарифа рисовалась, а в грид
    // «на что перейти» и в визард создания заведения этот тариф не попадал:
    // купить его самообслуживанием нельзя, `resolve_sellable_plan` отобьёт
    // checkout как unknown_plan. Источник поля — `_plan_dict` в
    // backend/subscriptions/views.py; пропадёт там — фильтры молча ослабнут.
    is_private: !!p.is_private,
    // Акция с очередью: при идущем сроке любого платного тарифа (и Start — решение
    // владельца 19.10.2026) оплачивается сразу и начинается после текущего. PlanCard
    // ведёт такую карточку в оплату — без поля
    // равная цена уводила её в «переход с конца периода», который бэк отбивает
    // (`request_plan_change` → payment_required). Источник — `_plan_dict`.
    starts_after_current: !!p.starts_after_current,
    sale_ends_at: p.sale_ends_at || null,   // конец окна продаж акции (не включительно)
  };
}

function adaptPayment(p) {
  return {
    id: p.id != null ? p.id : (p.payment_id != null ? p.payment_id : p.invoice_id),
    invoice_id: p.invoice_id,
    kind: p.kind,
    plan_slug: p.plan_slug || p.plan,
    status: p.status,
    amount_rub: num(p.amount_rub),
    created_at: p.created_at,
    paid_at: p.paid_at,
    failed_at: p.failed_at,
    // _payment_dict не отдаёт failure_reason (хранится в meta) → обычно undefined,
    // экран CheckoutFail прячет строку «Причина», когда её нет.
    failure_reason: p.failure_reason ?? null,
    // Отказ ещё не окончательный: статус поставила наша догадка о неоплате
    // (браузерный FailURL, свип протухших), и настоящий ResultURL её отменит.
    // Источник — `is_provisional` в `_payment_dict`; пропадёт там — CheckoutFail
    // молча вернётся к приговору с первого кадра (шрам S13).
    is_provisional: !!p.is_provisional,
    // Почему pending-счёт — не «в обработке»: 'superseded' (счёт на продление заменён —
    // период оплачен иначе, выставлен новый или срок сдвинут; платить не нужно) или
    // 'review' (Точка подтвердила оплату, её разбирает человек); иначе null. Источник —
    // `pending_reason` в `_payment_dict` (backend/subscriptions/views.py).
    pending_reason: p.pending_reason || null,
    // Этап 2: PDF закрывающего акта (только оплаченный безнал Точки; иначе null).
    act_pdf_url: p.act_pdf_url ?? null,
    // {id, name} пространства, созданного оплатой по именной ссылке в режиме
    // «новое пространство»; иначе null. По нему страница успеха переключает
    // клиента в купленное заведение — иначе он вернулся бы в приложение и не
    // нашёл того, за что заплатил.
    created_workspace: p.created_workspace || null,
    // Оплаченная акция встала в очередь за текущим тарифом — дата её старта;
    // иначе null. По ней страница успеха не обещает «подписка активна».
    queued_starts_at: p.queued_starts_at || null,
    needs_review: p.needs_review || null,   // вторая оплата акции: «вернём лишний платёж»
  };
}

async function getSubscription(workspaceId) {
  return adaptSubscription(await unwrap(api.getSubscription(workspaceId)));
}
async function listPlans() {
  const d = await unwrap(api.listPlans());
  return (d.plans || []).map(adaptPlan);
}
async function listPayments(workspaceId) {
  const d = await unwrap(api.listPayments(workspaceId));
  return (d.payments || []).map(adaptPayment);
}
// expectedPriceRub — цена из окна согласия. 409 price_changed: ошибка несёт актуальную
// (`price_rub`) и показанную (`expected_price_rub`) цену числами — по ним экран заново
// открывает окно с новой ценой и пояснением «было → стало».
async function changePlan(slug, workspaceId, consent, expectedPriceRub) {
  const res = await api.changePlan(slug, workspaceId, consent, expectedPriceRub);
  try {
    return adaptSubscription(await unwrap(Promise.resolve(res)));
  } catch (e) {
    const d = res && res.data;
    if (e && e.code === 'price_changed' && d && typeof d === 'object') {
      e.price_rub = d.price_rub != null ? num(d.price_rub) : null;
      e.expected_price_rub = d.expected_price_rub != null ? num(d.expected_price_rub) : null;
    }
    throw e;
  }
}
async function cancelPendingChange(workspaceId) {
  return adaptSubscription(await unwrap(api.cancelPendingChange(workspaceId)));
}
// Выключение при ждущем счёте Точки на продление: сервер добавляет `notice` — счёт
// остаётся действительным, его оплата засчитает период, но продление не включит (S42).
// Нет ключа (счёта нет, старый бэкенд) → null.
async function setAutoRenew(enabled, workspaceId) {
  const d = await unwrap(api.setAutoRenew(enabled, workspaceId));
  const notice = d && typeof d.notice === 'string' && d.notice.trim() ? d.notice : null;
  return { ...adaptSubscription(d), notice };
}
// checkout → {invoice_id, payment_id, redirect_url}; UI ведёт на redirect_url (mock-gateway за DEBUG).
// method: 'foreign_card' — оплата из-за рубежа (нерекуррентно), иначе обычная карта.
// consent: true — галочка согласия на автосписания стоит (RecurringConsentModal).
async function startCheckout(slug, workspaceId, method, consent) {
  return unwrap(api.startCheckout(slug, workspaceId, method, consent));
}
async function getPaymentStatus(invoiceId, workspaceId) {
  return adaptPayment(await unwrap(api.getPaymentStatus(invoiceId, workspaceId)));
}
async function mockPaymentCallback(paymentId, outcome) {
  return unwrap(api.mockPaymentCallback(paymentId, outcome));
}

// Счёт (Точка): нормализуем amount_rub (DRF-строка) → число; остальное — как отдаёт бэк.
function adaptInvoice(inv) {
  return {
    invoice_id: inv.invoice_id,
    document_id: inv.document_id,
    number: inv.number,
    status: inv.status,                 // pending | succeeded | failed | cancelled
    paid: !!inv.paid,
    amount_rub: num(inv.amount_rub),
    plan_slug: inv.plan_slug,
    created_at: inv.created_at,
    paid_at: inv.paid_at,
    failure_reason: inv.failure_reason ?? null,
    // из checkout-ответа (201) — payment_id/pdf_url; на GET их нет → undefined.
    payment_id: inv.payment_id,
    pdf_url: inv.pdf_url,
    // Этап 2: закрывающий акт {number, date, pdf_url} | null (появляется после оплаты).
    act: inv.act || null,
    // Акция в очереди / оплата на разбор — те же признаки, что у карточного платежа.
    queued_starts_at: inv.queued_starts_at || null,
    needs_review: inv.needs_review || null,
  };
}

// POST /invoice/checkout/ → checkout-ответ (payment_id/invoice_id/document_id/number/amount_rub/status/pdf_url).
async function startInvoiceCheckout(slug, requisites, offerAccepted, workspaceId) {
  return adaptInvoice(await unwrap(api.startInvoiceCheckout(slug, requisites, offerAccepted, workspaceId)));
}
async function getInvoice(invoiceId, workspaceId) {
  return adaptInvoice(await unwrap(api.getInvoice(invoiceId, workspaceId)));
}

/* ── Состояние оплаты по ТОКЕНУ именной ссылки ──
   Счёт на существующее пространство оплачивает кто угодно, в том числе без
   аккаунта, — а `/payments/<id>/` и `/invoice/<id>/` скоупятся по сессии и
   такому плательщику отвечают 404. Форму ответа держим ту же (adaptPayment /
   adaptInvoice), чтобы экраны возврата отличались ТОЛЬКО источником данных:
   вторая ветка разбора разъехалась бы ровно там, где человек выясняет, прошли
   ли его деньги. */
async function getLinkPaymentStatus(token, invoiceId) {
  return adaptPayment(await unwrap(linkApi.getPaymentLinkPaymentStatus(token, invoiceId)));
}
async function getLinkInvoice(token, invoiceId) {
  return adaptInvoice(await unwrap(linkApi.getPaymentLinkInvoice(token, invoiceId)));
}
// POST /invoice/<id>/email/ → {sent:true}.
async function sendInvoiceEmail(invoiceId, email, workspaceId) {
  return unwrap(api.sendInvoiceEmail(invoiceId, email, workspaceId));
}
// POST /invoice/<id>/act/email/ → {sent:true} (этап 2, закрывающий акт).
async function sendActEmail(invoiceId, email, workspaceId) {
  return unwrap(api.sendActEmail(invoiceId, email, workspaceId));
}
// GET /payer-requisites/ — сохранённые реквизиты плательщика (префилл формы счёта).
async function getPayerRequisites(workspaceId) {
  return unwrap(api.getPayerRequisites(workspaceId));
}

async function dismissPersonalWarning() { return unwrap(api.dismissPersonalWarning()); }

export const subscriptionReal = {
  getSubscription, listPlans, listPayments, changePlan, cancelPendingChange,
  dismissPersonalWarning,
  setAutoRenew,
  startCheckout, getPaymentStatus, mockPaymentCallback,
  startInvoiceCheckout, getInvoice, sendInvoiceEmail, sendActEmail,
  getLinkPaymentStatus, getLinkInvoice,
  getPayerRequisites,
};
